import {Component, OnDestroy, OnInit, signal, Signal, ViewChild} from '@angular/core';
import {IChartConfig, IChatResponse, IStyleChart, TypeChart} from "../../../shared/interfaces/chat";
import {ChartOptions} from "chart.js";
import {GraphicsAssistant} from "../../../shared/clases/graphicsAssistant";
import {firstValueFrom, forkJoin, Observable} from "rxjs";
import {CustomTableComponent, CustomTitle, DataColumnType, SafeResourcePipe} from "lib-base";
import {TranslateService} from "@ngx-translate/core";
import {MessageService} from "primeng/api";
import {ChatService} from "../../../services/chat.service";
import {InputTextareaModule} from "primeng/inputtextarea";
import {FormsModule} from "@angular/forms";
import {ButtonDirective} from "primeng/button";
import {SkeletonModule} from "primeng/skeleton";
import {ChartModule} from "primeng/chart";
import {NgClass} from "@angular/common";

@Component({
  selector: 'app-chat-pqrsd',
  standalone: true,
  imports: [
    InputTextareaModule,
    FormsModule,
    ButtonDirective,
    SafeResourcePipe,
    CustomTableComponent,
    CustomTitle,
    SkeletonModule,
    ChartModule,
    NgClass
  ],
  templateUrl: './chat-pqrsd.component.html',
  styleUrl: './chat-pqrsd.component.scss'
})
export class ChatPqrsdComponent  implements OnInit, OnDestroy {

  translation: any = {};
  inputText: string = '';
  responseText: string = '';
  maxlength: number = 1_000;
  configTable: any = {};
  isLoading: boolean = false;

   response: IChatResponse | undefined = undefined;
  title: Signal<string> = signal('');

  private config: IChartConfig | undefined;
  configChart: ChartOptions | undefined;
  private styleChart: IStyleChart | undefined;
  readonly BACKGROUND = '#eceff4';
  charts: GraphicsAssistant[] = [];
  requestMethod$!: (input: string) => Observable<IChatResponse>;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;

  constructor(private _t: TranslateService,
              private messageService: MessageService,
              private chatService: ChatService) {
    this.getStyleChart();
  }


  ngOnInit(): void {
    this.modifyBackgroundCss();
    this.requestMethod$ = this.chatService.simpleResponsePqrsdf.bind(this.chatService);
    this.translations()
      .then(() => {
          this.title = signal(this.translation.title);

      });

  }

  ngOnDestroy(): void {
    this.modifyBackgroundCss(this.BACKGROUND);
  }


  async translations() {
    this.translation = await firstValueFrom(forkJoin({
      title: this._t.get('components.app-chat.title-pqrsdf'),
      subtitle: this._t.get('components.app-chat.subtitle'),
      respuesta: this._t.get('components.app-chat.respuestas'),
      send: this._t.get('components.app-chat.send'),
      reload: this._t.get('components.app-chat.reload'),
      placeholder: this._t.get('components.app-chat.placeholder'),
      advertencia: this._t.get('common.advertencia'),
      camposRequeridos: this._t.get('components.app-chat.campo-requerido'),
      errorGeneral: this._t.get('components.app-chat.error-general'),
      descripcionCampo: this._t.get('components.app-chat.title-input'),
    }))
  }

  clean() {
    this.inputText = '';
    this.cleanBeforeNewRequest();
  }

  private cleanBeforeNewRequest() {
    this.responseText = '';
    this.response = undefined;
    this.config = undefined;
    this.configTable = {};
    this.configChart = undefined;
    this.charts = [];
  }

  getResponse() {
    if (this.validateInput()) {
      this.isLoading = true;
      this.cleanBeforeNewRequest();
      this.requestMethod$(this.inputText)
        .subscribe({
          next: result => {
            this.isLoading = false;
            this.response = result;
            this.responseText = result.message;
            this.getDataTable();
            this.setConfigChartByData();
          }, error: () => {
            this.isLoading = false;
            this.messageService.add({
              severity: 'warn',
              summary: this.translation.advertencia,
              detail: this.translation.errorGeneral,
            });
          }
        });
    }
  }

  validateInput(): boolean {
    if (!this.inputText || !this.inputText.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translation.advertencia,
        detail: this.translation.camposRequeridos,
      });
      return false;
    }
    return true;
  }

  getDataTable() {
    if (this.response && this.response.data) {
      this.getColumnsTable(this.response.data.types);
      setTimeout(() => {
        if (this.customTableComponent) {
          this.customTableComponent.data = this.response?.data?.values || [];
        }
      }, 100);
    }
  }

  getColumnsTable(types: any) {
    const columns: any[] = [];
    Object.entries(types)
      .forEach(([key, value]) => {
        const field = {
          field: key,
          header: key,
          class: 'font-13 text-white',
          columnmHeader: {
            class: 'text-left text-white font-13 font-bold bg-black-alpha-90',
            style:  ''
          },
          sort: true,
          columnFilter: (value==="string"),
          dataType: this.getTypes(value)
        };
        columns.push(field);
      });
    this.configTable.columns = columns;
  }

  private getTypes(value: any) {
    switch (value) {
      case 'string':
        return DataColumnType.TEXT;
      case 'number':
        return DataColumnType.NUMBER;
      case 'date':
        return DataColumnType.DATE;
      case 'percent':
        return DataColumnType.PERCENT;
      default:
        return DataColumnType.TEXT;
    }
  }

  setConfigChartByData() {
    if (!this.response || !this.response.chartConfig) return;
    const config = this.response.chartConfig;
    this.config = {
      typeChart: config?.typeChart || 'bar',
      stacked: config?.stacked,
      cutoutPercentage: config?.cutoutPercentage,
      nombreDataSet: config?.nombreDataSet,
      orientation: config?.orientation || 'x',
      xAxis: config?.xAxis || '',
      yAxis: config?.yAxis || [],
      groups: config.groups
    };
    this.generateChart();
  }

  generateChart() {
    if (!this.config) return;
    const data: any[] = this.response?.data?.values ?? [];
    let dataGroup;
    if (this.config?.groups) {
      dataGroup = GraphicsAssistant.groupArray(data, this.config.groups);
      const config = this.config;
      Object.entries(dataGroup).forEach(([key, value]) => {
        const grafico = new GraphicsAssistant();
        grafico.title = key;
        grafico.generateChart(config, (value as any[]));
        this.charts.push(grafico);
      })
    } else {
      const grafico = new GraphicsAssistant();
      grafico.generateChart(this.config, data);
      this.charts.push(grafico);
    }

  }

  getStyleChart() {

    const documentStyle = getComputedStyle(document.documentElement);
    this.styleChart = {
      textColor: documentStyle.getPropertyValue('--text-color'),
      textColorSecondary: documentStyle.getPropertyValue('--text-color-secondary'),
      surfaceBorder: documentStyle.getPropertyValue('--surface-border')
    }

  }

  private modifyBackgroundCss = (color?: any) => {
    const bg = color ? color : '#000';
    const div = document.getElementById('layout-content');
    if (div) {
      div.style.background = bg;
    }
  }

  get typeChart(): Exclude<TypeChart, 'stacked'> | null {
    if (!this.config) return null;
    if (this.config.typeChart === 'stacked') return "bar";
    return this.config?.typeChart || 'bar';
  }
}
